import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DashboardMetricCardComponent } from '../../../../../../shared/components/dashboard-metric-card/dashboard-metric-card.component';
import { AlertComponent } from '../../../../../../shared/components/alert/alert.component';
import { ActionButtonComponent } from '../../../../../../shared/components/action-button/action-button.component';
import { StationsModel } from '../../../../../stations/models/stations';
import { ApiMessageService } from '../../../../../../core/services/notification/api-message.service';
import { HomePageApiService } from '../../services/api/home-page-api.service';
import { MonthlyHistoryModel } from '../../models/monthly-history';
import { MonthlyAverageModel } from '../../models/monthly-average';

@Component({
  selector: 'app-meteorological-data',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DashboardMetricCardComponent,
    AlertComponent,
    ActionButtonComponent,
  ],
  templateUrl: './meteorological-data.component.html',
  styleUrls: ['./meteorological-data.component.css'],
})
export class MeteorologicalDataComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() stations: StationsModel[] = [];
  @Input() nearestStationId: number | string | null = null;

  // Propriedades para Dados do Mês Corrente
  currentMonthName: string = '';
  currentMonthNameAbbr: string = '';
  currentYear: number = new Date().getFullYear();
  isLoadingCurrentMonth: boolean = false;

  // Valores do mês corrente (valores mockados)
  currentMonthAvgHistoricalTemp: string = '24.5 °C';
  currentMonthAvgAccumulatedRain: string = '145.2 mm';
  currentMonthAccumulatedRain: string = '152.8 mm';
  currentMonthAvgTemp: string = '23.8 °C';

  // Propriedades para Filtros Personalizados
  selectedFilterStationId: string = '';
  selectedFilterMonth: string = '';
  selectedFilterYear: string = '';
  selectedFilterMonthNameAbbr: string = '';
  availableYears: number[] = [];
  showFilteredResults: boolean = false;
  isLoadingFiltered: boolean = false;

  // Valores filtrados (valores mockados)
  filteredAvgHistoricalTemp: string = '0 °C';
  filteredAvgAccumulatedRain: string = '0 mm';
  filteredAccumulatedRain: string = '0 mm';
  filteredAvgTemp: string = '0 °C';

  // Tipos dos cards históricos (primary ou danger)
  filteredAvgHistoricalTempType: 'primary' | 'danger' = 'primary';
  filteredAvgAccumulatedRainType: 'primary' | 'danger' = 'primary';
  // Tipos dos cards do mês/ano específico (primary ou danger)
  filteredAccumulatedRainType: 'primary' | 'danger' = 'primary';
  filteredAvgTempType: 'primary' | 'danger' = 'primary';

  // Propriedades para Alertas
  alert: { type: string; message: string; icon: string } | null = null;
  private alertSubscription!: Subscription;

  constructor(
    private readonly msgService: ApiMessageService,
    private readonly homePageApiService: HomePageApiService,
  ) {}

  ngOnInit() {
    this.initializeCurrentMonthData();
    this.initializeAvailableYears();
    this.subscribeToAlerts();
    this.initializeDefaultFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando o ID da estação mais próxima for recebido, pré-seleciona no dropdown
    if (changes['nearestStationId']?.currentValue) {
      this.selectedFilterStationId =
        changes['nearestStationId'].currentValue.toString();

      // Carrega automaticamente os dados após pré-selecionar a estação
      // Usa setTimeout para garantir que os filtros de mês e ano estejam inicializados
      setTimeout(() => {
        this.loadFilteredDataAutomatically();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    if (this.alertSubscription) {
      this.alertSubscription.unsubscribe();
    }
  }

  /**
   * Inicializa os filtros com valores padrão (mês e ano atuais)
   */
  private initializeDefaultFilters(): void {
    const now = new Date();
    this.selectedFilterMonth = (now.getMonth() + 1).toString(); // Mês atual (1-12)
    this.selectedFilterYear = now.getFullYear().toString(); // Ano atual
  }

  /**
   * Subscreve aos alertas do serviço de mensagens
   */
  private subscribeToAlerts(): void {
    this.alertSubscription = this.msgService.alert$.subscribe((alert) => {
      this.alert = alert;
      if (alert) {
        setTimeout(() => this.msgService.clear(), 5000);
      }
    });
  }

  /**
   * Fecha o alerta
   */
  closeAlert(): void {
    this.msgService.clear();
  }

  /**
   * Inicializa os dados do mês corrente
   */
  private initializeCurrentMonthData(): void {
    const now = new Date();
    const monthNames = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ];
    const monthNamesAbbr = [
      'Jan',
      'Fev',
      'Mar',
      'Abr',
      'Mai',
      'Jun',
      'Jul',
      'Ago',
      'Set',
      'Out',
      'Nov',
      'Dez',
    ];
    this.currentMonthName = monthNames[now.getMonth()];
    this.currentMonthNameAbbr = monthNamesAbbr[now.getMonth()];
    this.currentYear = now.getFullYear();

    this.isLoadingCurrentMonth = true;
    setTimeout(() => {
      this.isLoadingCurrentMonth = false;
    }, 500);
  }

  /**
   * Inicializa a lista de anos disponíveis (do ano atual até 2022)
   * Quando o ano muda, o novo ano é automaticamente incluído
   */
  private initializeAvailableYears(): void {
    const currentYear = new Date().getFullYear();
    const minYear = 2022;
    this.availableYears = [];

    for (let year = currentYear; year >= minYear; year--) {
      this.availableYears.push(year);
    }
  }

  /**
   * Retorna o nome abreviado do mês baseado no número
   */
  private getMonthNameAbbr(monthNumber: string): string {
    const monthNamesAbbr = [
      'Jan',
      'Fev',
      'Mar',
      'Abr',
      'Mai',
      'Jun',
      'Jul',
      'Ago',
      'Set',
      'Out',
      'Nov',
      'Dez',
    ];
    const index = Number.parseInt(monthNumber, 10) - 1;
    return monthNamesAbbr[index] || '';
  }

  /**
   * Aplica o filtro personalizado
   */
  applyCustomFilter(): void {
    this.loadMeteorologicalData(
      () => {
        // Callback de validação: exibe mensagem de advertência
        const mensagem = this.getMensagemCamposFaltantes();
        this.msgService.showWarning(mensagem);
      },
      (error) => {
        // Callback de erro: exibe mensagem de erro
        let mensagem = 'Erro ao buscar dados meteorológicos. Tente novamente.';

        if (error.status === 500) {
          mensagem = 'Erro interno no servidor. Tente novamente mais tarde.';
        } else if (error.error?.message) {
          mensagem = error.error.message;
        }

        this.msgService.showError(mensagem);
      },
    );
  }

  /**
   * Carrega os dados filtrados automaticamente quando a estação mais próxima é detectada
   * Este método não exibe alertas de validação pois é chamado internamente
   */
  private loadFilteredDataAutomatically(): void {
    this.loadMeteorologicalData();
  }

  /**
   * Método privado que centraliza a lógica de carregamento de dados meteorológicos
   * @param onValidationError Callback executado quando a validação dos campos falha
   * @param onApiError Callback executado quando ocorre erro na chamada da API
   */
  private loadMeteorologicalData(
    onValidationError?: () => void,
    onApiError?: (error: any) => void,
  ): void {
    // Validação dos campos obrigatórios
    if (
      !this.selectedFilterStationId ||
      !this.selectedFilterMonth ||
      !this.selectedFilterYear
    ) {
      onValidationError?.();
      return;
    }

    this.isLoadingFiltered = true;
    this.selectedFilterMonthNameAbbr = this.getMonthNameAbbr(
      this.selectedFilterMonth,
    );

    // Busca dados de média histórica mensal e média mensal em paralelo
    // Cada API é tratada independentemente para permitir que uma funcione mesmo se a outra falhar
    forkJoin({
      monthlyHistory: this.homePageApiService
        .getMonthlyHistory(
          Number(this.selectedFilterStationId),
          Number(this.selectedFilterMonth),
        )
        .pipe(
          catchError((error) => {
            // Retorna valores padrão se a API de histórico falhar
            return of({
              stationId: Number(this.selectedFilterStationId),
              month: Number(this.selectedFilterMonth),
              averageTemperature: 0,
              averageRainAccum: 0,
            } as MonthlyHistoryModel);
          }),
        ),
      monthlyAverage: this.homePageApiService
        .getMonthlyAverage(
          Number(this.selectedFilterStationId),
          Number(this.selectedFilterMonth),
          Number(this.selectedFilterYear),
        )
        .pipe(
          catchError((error) => {
            // Retorna valores padrão se a API de média mensal falhar
            return of({
              id: 0,
              stationId: Number(this.selectedFilterStationId),
              averageTypeName: 'MENSAL',
              year: Number(this.selectedFilterYear),
              month: Number(this.selectedFilterMonth),
              averageTemperature: 0,
              totalRain: 0,
              creationDate: '',
            } as MonthlyAverageModel);
          }),
        ),
    }).subscribe({
      next: (result) => {
        // Dados históricos
        const historyData = result.monthlyHistory;
        this.filteredAvgHistoricalTemp =
          historyData.averageTemperature === 0
            ? 'Histórico indisponível'
            : `${historyData.averageTemperature.toFixed(1)} °C`;
        this.filteredAvgAccumulatedRain =
          historyData.averageRainAccum === 0
            ? 'Histórico indisponível'
            : `${historyData.averageRainAccum.toFixed(1)} mm`;

        this.filteredAvgHistoricalTempType =
          historyData.averageTemperature === 0 ? 'danger' : 'primary';
        this.filteredAvgAccumulatedRainType =
          historyData.averageRainAccum === 0 ? 'danger' : 'primary';

        // Dados do mês/ano específico
        const averageData = result.monthlyAverage;
        this.filteredAvgTemp =
          averageData.averageTemperature === 0
            ? 'Dados indisponíveis'
            : `${averageData.averageTemperature.toFixed(1)} °C`;
        this.filteredAccumulatedRain =
          averageData.totalRain === 0
            ? 'Dados indisponíveis'
            : `${averageData.totalRain.toFixed(1)} mm`;

        this.filteredAvgTempType =
          averageData.averageTemperature === 0 ? 'danger' : 'primary';
        this.filteredAccumulatedRainType =
          averageData.totalRain === 0 ? 'danger' : 'primary';

        this.showFilteredResults = true;
        this.isLoadingFiltered = false;

        // Verifica se ambas as APIs retornaram zero
        const historicoZero =
          historyData.averageTemperature === 0 &&
          historyData.averageRainAccum === 0;
        const mensalZero =
          averageData.averageTemperature === 0 && averageData.totalRain === 0;

        if (historicoZero && mensalZero) {
          this.msgService.showWarning(
            'Nenhum dado encontrado para esta estação no mês/ano informado.',
          );
        }
      },
      error: (error) => {
        // Erro geral (não deveria acontecer com catchError, mas é bom ter)
        if (onApiError) {
          onApiError(error);
        }
        this.isLoadingFiltered = false;
      },
    });
  }

  /**
   * Retorna a mensagem apropriada baseada nos campos que estão faltando
   */
  private getMensagemCamposFaltantes(): string {
    const faltaEstacao = !this.selectedFilterStationId;
    const faltaMes = !this.selectedFilterMonth;
    const faltaAno = !this.selectedFilterYear;

    // Todos os campos faltando
    if (faltaEstacao && faltaMes && faltaAno) {
      return 'Por favor, selecione uma estação, um mês e um ano.';
    }

    // Dois campos faltando
    if (faltaEstacao && faltaMes) {
      return 'Por favor, selecione uma estação e um mês.';
    }
    if (faltaEstacao && faltaAno) {
      return 'Por favor, selecione uma estação e um ano.';
    }
    if (faltaMes && faltaAno) {
      return 'Por favor, selecione um mês e um ano.';
    }

    // Um campo faltando
    if (faltaEstacao) {
      return 'Por favor, selecione uma estação.';
    }
    if (faltaMes) {
      return 'Por favor, selecione um mês.';
    }
    if (faltaAno) {
      return 'Por favor, selecione um ano.';
    }

    return 'Por favor, preencha todos os campos obrigatórios.';
  }

  /**
   * Formata a descrição da estação com indicador de status
   */
  getStationDisplayText(station: StationsModel): string {
    const description = station.description || `Estação ${station.id}`;

    if (!station.active) {
      return `${description} - Estação Inativa`;
    }

    return description;
  }

  /**
   * Retorna o estilo CSS para o option da estação
   */
  getStationOptionStyle(station: StationsModel): string {
    if (!station.active) {
      return 'background-color: #ffebee; color: #c62828; font-weight: 500;';
    }

    return ''; // Sem estilo especial para estações ativas
  }
}
